import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LayoutDashboard, Users, Calendar, CreditCard, Bell, LogOut,
  Image, Download, Copy, Sparkles, Facebook, RefreshCw, Check,
  FileImage, History, Wand2, Clock, Plus, Trash2, Save, Play, Upload, X
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import AdminSidebar from "@/components/AdminSidebar";
import { useNotificationCount } from "@/hooks/useNotificationCount";

const API = "/api";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a07fe825-978d-42af-ad0d-66b05bf66e9f/artifacts/6h7nm3ap_Photoroom_20260215_225825.png";

// Preset style options
const STYLE_PRESETS = [
  { name: "Neon Green", description: "Vibrant neon green accents, dark background, modern and energetic" },
  { name: "Professional", description: "Clean, corporate style with green grass imagery, trustworthy feel" },
  { name: "Tropical Okinawa", description: "Tropical vibes, palm trees, hibiscus flowers, island paradise feel" },
  { name: "Military Friendly", description: "Patriotic accents, professional, appeals to military families" },
  { name: "Summer Sale", description: "Bright sunny colors, summer vibes, energetic and fresh" },
  { name: "Ninja Theme", description: "Dark and sleek, ninja imagery, stealthy and efficient" },
  { name: "Custom", description: "Describe your own style below" },
];

const FlyerGenerator = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const notificationCount = useNotificationCount();
  
  // Form state
  const [headline, setHeadline] = useState("SPRING SPECIAL!");
  const [offerText, setOfferText] = useState("20% OFF Your First Cut");
  const [tagline, setTagline] = useState("Not Silent, But Swift!");
  const [businessName, setBusinessName] = useState("Zig Zag Lawncare Okinawa");
  const [contactInfo, setContactInfo] = useState("Contact us on Facebook @zigzagokinawa");
  const [servicesText, setServicesText] = useState("Professional Lawn Care • On & Off Base Service • Best Prices Guaranteed");
  const [selectedPreset, setSelectedPreset] = useState("Neon Green");
  const [customStyle, setCustomStyle] = useState("");
  const [includeContact, setIncludeContact] = useState(true);
  const [includeServices, setIncludeServices] = useState(true);
  
  // Tagline presets
  const TAGLINE_PRESETS = [
    "Not Silent, But Swift!",
    "Your Ultimate Grass Ninja",
    "Slice Through Your Lawn Care Worries",
    "Precision Cuts, Ninja Speed",
    "Okinawa's #1 Lawn Care",
  ];
  
  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [generatedCaption, setGeneratedCaption] = useState("");
  const [captionCopied, setCaptionCopied] = useState(false);
  
  // History state
  const [flyerHistory, setFlyerHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Auto-post schedule state
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleTime, setScheduleTime] = useState("09:00");
  const [scheduleDays, setScheduleDays] = useState(["monday", "wednesday", "friday"]);
  const [headlinePool, setHeadlinePool] = useState([
    { headline: "SPRING SPECIAL!", offer: "20% OFF Your First Cut" },
    { headline: "SUMMER SAVINGS!", offer: "Free Edging with Any Service" },
    { headline: "NEW CUSTOMER DEAL!", offer: "15% OFF Your First Month" },
  ]);
  const [newHeadline, setNewHeadline] = useState("");
  const [newOffer, setNewOffer] = useState("");
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [pendingFlyers, setPendingFlyers] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);

  // Logo state
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFilename, setLogoFilename] = useState("");
  const [loadingLogo, setLoadingLogo] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPosition, setLogoPosition] = useState("bottom-right");
  const [logoScale, setLogoScale] = useState(0.15);

  const DAYS_OF_WEEK = [
    { value: "monday", label: "Mon" },
    { value: "tuesday", label: "Tue" },
    { value: "wednesday", label: "Wed" },
    { value: "thursday", label: "Thu" },
    { value: "friday", label: "Fri" },
    { value: "saturday", label: "Sat" },
    { value: "sunday", label: "Sun" },
  ];


  useEffect(() => {
    fetchHistory();
    fetchScheduleSettings();
    fetchPendingFlyers();
    fetchLogo();
  }, []);

  const fetchLogo = async () => {
    setLoadingLogo(true);
    try {
      const response = await axios.get(`${API}/flyers/logo`, { withCredentials: true });
      if (response.data.has_logo) {
        setLogoPreview(`data:${response.data.mime_type};base64,${response.data.data}`);
        setLogoFilename(response.data.filename);
      }
    } catch (error) {
      console.error("Failed to fetch logo:", error);
    } finally {
      setLoadingLogo(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file (PNG, JPG, etc.)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Logo must be under 5MB");
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/flyers/logo`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Show preview
      const reader = new FileReader();
      reader.onload = (ev) => setLogoPreview(ev.target.result);
      reader.readAsDataURL(file);
      setLogoFilename(file.name);
      toast.success("Logo saved! It will be added to all future flyers.");
    } catch (error) {
      console.error("Logo upload failed:", error);
      toast.error("Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await axios.delete(`${API}/flyers/logo`, { withCredentials: true });
      setLogoPreview(null);
      setLogoFilename("");
      toast.success("Logo removed");
    } catch (error) {
      toast.error("Failed to remove logo");
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/flyers/history`, { withCredentials: true });
      setFlyerHistory(response.data || []);
    } catch (error) {
      console.error("Failed to fetch flyer history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchScheduleSettings = async () => {
    try {
      const response = await axios.get(`${API}/flyers/schedule`, { withCredentials: true });
      if (response.data) {
        setScheduleEnabled(response.data.enabled || false);
        setScheduleTime(response.data.time || "09:00");
        setScheduleDays(response.data.days || ["monday", "wednesday", "friday"]);
        setHeadlinePool(response.data.headline_pool || [
          { headline: "SPRING SPECIAL!", offer: "20% OFF Your First Cut" },
        ]);
      }
    } catch (error) {
      console.error("Failed to fetch schedule settings:", error);
    }
  };

  const fetchPendingFlyers = async () => {
    setLoadingPending(true);
    try {
      const response = await axios.get(`${API}/flyers/pending`, { withCredentials: true });
      setPendingFlyers(response.data || []);
    } catch (error) {
      console.error("Failed to fetch pending flyers:", error);
    } finally {
      setLoadingPending(false);
    }
  };

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try {
      await axios.put(`${API}/flyers/schedule`, {
        enabled: scheduleEnabled,
        time: scheduleTime,
        days: scheduleDays,
        headline_pool: headlinePool,
      }, { withCredentials: true });
      toast.success("Schedule settings saved!");
    } catch (error) {
      console.error("Failed to save schedule:", error);
      toast.error("Failed to save schedule settings");
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleAddToPool = () => {
    if (!newHeadline.trim() || !newOffer.trim()) {
      toast.error("Please enter both headline and offer");
      return;
    }
    setHeadlinePool([...headlinePool, { headline: newHeadline.trim(), offer: newOffer.trim() }]);
    setNewHeadline("");
    setNewOffer("");
    toast.success("Added to content pool!");
  };

  const handleRemoveFromPool = (index) => {
    const updated = headlinePool.filter((_, i) => i !== index);
    setHeadlinePool(updated);
  };

  const toggleDay = (day) => {
    if (scheduleDays.includes(day)) {
      setScheduleDays(scheduleDays.filter(d => d !== day));
    } else {
      setScheduleDays([...scheduleDays, day]);
    }
  };

  const handleGenerateNow = async () => {
    if (headlinePool.length === 0) {
      toast.error("Please add at least one headline/offer to the content pool");
      return;
    }
    
    setIsGenerating(true);
    try {
      const response = await axios.post(`${API}/flyers/generate-scheduled`, {}, { withCredentials: true });
      if (response.data.success) {
        toast.success("Flyer generated and ready to post!");
        fetchPendingFlyers();
        fetchHistory();
      }
    } catch (error) {
      console.error("Failed to generate scheduled flyer:", error);
      toast.error(error.response?.data?.detail || "Failed to generate flyer");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleMarkAsPosted = async (flyerId) => {
    try {
      await axios.put(`${API}/flyers/${flyerId}/mark-posted`, {}, { withCredentials: true });
      toast.success("Marked as posted!");
      fetchPendingFlyers();
    } catch (error) {
      console.error("Failed to mark as posted:", error);
      toast.error("Failed to update flyer status");
    }
  };

  const getStyleDescription = () => {
    if (selectedPreset === "Custom") {
      return customStyle;
    }
    const preset = STYLE_PRESETS.find(p => p.name === selectedPreset);
    return preset?.description || "";
  };

  const handleGenerate = async () => {
    if (!headline.trim() || !offerText.trim()) {
      toast.error("Please fill in headline and offer text");
      return;
    }

    setIsGenerating(true);
    setGeneratedImage(null);
    setGeneratedCaption("");

    try {
      const response = await axios.post(`${API}/flyers/generate`, {
        headline: headline.trim(),
        offer_text: offerText.trim(),
        tagline: tagline === "custom" ? customStyle : tagline,
        business_name: businessName.trim(),
        contact_info: contactInfo.trim(),
        services_text: servicesText.trim(),
        style_description: getStyleDescription(),
        include_contact: includeContact,
        include_services: includeServices,
        logo_position: logoPosition,
        logo_scale: logoScale,
      }, { withCredentials: true });

      if (response.data.success) {
        // Set the full base64 image data
        setGeneratedImage(`data:${response.data.image.mime_type};base64,${response.data.image.full_data}`);
        setGeneratedCaption(response.data.caption);
        toast.success("Flyer generated successfully!");
        fetchHistory(); // Refresh history
      }
    } catch (error) {
      console.error("Flyer generation failed:", error);
      toast.error(error.response?.data?.detail || "Failed to generate flyer");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;
    
    const link = document.createElement("a");
    link.href = generatedImage;
    link.download = `zigzag-flyer-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Flyer downloaded!");
  };

  const handleCopyCaption = () => {
    if (!generatedCaption) return;
    
    navigator.clipboard.writeText(generatedCaption);
    setCaptionCopied(true);
    toast.success("Caption copied to clipboard!");
    setTimeout(() => setCaptionCopied(false), 2000);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      <AdminSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} notificationCount={notificationCount} />

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-y-auto">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold text-white uppercase flex items-center gap-3">
                <Wand2 className="w-8 h-8 text-[#39FF14]" />
                Flyer Generator
              </h1>
              <p className="text-[#A1A1AA] mt-1">Create AI-powered promotional flyers for Facebook</p>
            </div>
          </div>

          <Tabs defaultValue="create" className="space-y-6">
            <TabsList className="bg-[#121212] border border-[#27272A]">
              <TabsTrigger value="create" className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-black">
                <Sparkles className="w-4 h-4 mr-2" />
                Create Flyer
              </TabsTrigger>
              <TabsTrigger value="schedule" className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-black">
                <Clock className="w-4 h-4 mr-2" />
                Auto-Post
              </TabsTrigger>
              <TabsTrigger value="pending" className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-black flex items-center gap-2">
                <Facebook className="w-4 h-4" />
                Ready to Post
                {pendingFlyers.length > 0 && (
                  <span className="bg-[#39FF14] text-black text-xs px-1.5 py-0.5 rounded-full font-bold">
                    {pendingFlyers.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="history" className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-black">
                <History className="w-4 h-4 mr-2" />
                History
              </TabsTrigger>
            </TabsList>

            <TabsContent value="create">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Form Section */}
                <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6 space-y-6">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Image className="w-5 h-5 text-[#39FF14]" />
                    Flyer Details
                  </h2>

                  {/* Headline */}
                  <div className="space-y-2">
                    <Label className="text-white">Headline</Label>
                    <Input
                      value={headline}
                      onChange={(e) => setHeadline(e.target.value)}
                      placeholder="e.g., SPRING SPECIAL!"
                      className="bg-[#0A0A0A] border-[#27272A] text-white"
                      data-testid="flyer-headline"
                    />
                  </div>

                  {/* Offer Text */}
                  <div className="space-y-2">
                    <Label className="text-white">Offer/Discount</Label>
                    <Input
                      value={offerText}
                      onChange={(e) => setOfferText(e.target.value)}
                      placeholder="e.g., 20% OFF Your First Cut"
                      className="bg-[#0A0A0A] border-[#27272A] text-white"
                      data-testid="flyer-offer"
                    />
                  </div>

                  {/* Tagline/Catchphrase */}
                  <div className="space-y-2">
                    <Label className="text-white">Tagline/Catchphrase</Label>
                    <Select value={tagline} onValueChange={setTagline}>
                      <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white">
                        <SelectValue placeholder="Select a tagline" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272A]">
                        {TAGLINE_PRESETS.map((t) => (
                          <SelectItem key={t} value={t} className="text-white hover:bg-[#27272A]">
                            {t}
                          </SelectItem>
                        ))}
                        <SelectItem value="custom" className="text-white hover:bg-[#27272A]">
                          Custom tagline...
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {tagline === "custom" && (
                      <Input
                        value={customStyle}
                        onChange={(e) => setTagline(e.target.value)}
                        placeholder="Enter your custom tagline"
                        className="bg-[#0A0A0A] border-[#27272A] text-white mt-2"
                      />
                    )}
                  </div>

                  {/* Business Name */}
                  <div className="space-y-2">
                    <Label className="text-white">Business Name</Label>
                    <Input
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      placeholder="Zig Zag Lawncare Okinawa"
                      className="bg-[#0A0A0A] border-[#27272A] text-white"
                      data-testid="flyer-business-name"
                    />
                  </div>

                  {/* Contact Info (collapsible) */}
                  <div className="space-y-2">
                    <Label className="text-white">Contact Info (for flyer)</Label>
                    <Input
                      value={contactInfo}
                      onChange={(e) => setContactInfo(e.target.value)}
                      placeholder="Contact us on Facebook @zigzagokinawa"
                      className="bg-[#0A0A0A] border-[#27272A] text-white"
                      data-testid="flyer-contact-info"
                    />
                  </div>

                  {/* Services Text */}
                  <div className="space-y-2">
                    <Label className="text-white">Services Line</Label>
                    <Input
                      value={servicesText}
                      onChange={(e) => setServicesText(e.target.value)}
                      placeholder="Professional Lawn Care • On & Off Base Service"
                      className="bg-[#0A0A0A] border-[#27272A] text-white"
                      data-testid="flyer-services-text"
                    />
                  </div>

                  {/* Logo Upload */}
                  <div className="space-y-3 pt-4 border-t border-[#27272A]">
                    <Label className="text-white flex items-center gap-2">
                      <Upload className="w-4 h-4 text-[#39FF14]" />
                      Logo Overlay
                    </Label>
                    <p className="text-[#A1A1AA] text-xs">Upload a PNG logo to automatically overlay on all generated flyers.</p>

                    {logoPreview ? (
                      <div className="flex items-center gap-3 p-3 bg-[#0A0A0A] rounded-lg border border-[#27272A]">
                        <img
                          src={logoPreview}
                          alt="Logo"
                          className="w-14 h-14 object-contain rounded bg-[#121212] p-1"
                          data-testid="logo-preview"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm truncate">{logoFilename}</p>
                          <p className="text-[#39FF14] text-xs">Saved as default</p>
                        </div>
                        <div className="flex gap-1">
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={handleLogoUpload}
                              data-testid="logo-replace-input"
                            />
                            <span className="inline-flex items-center px-2 py-1 text-xs rounded bg-[#27272A] text-white hover:bg-[#39FF14] hover:text-black transition-colors">
                              Replace
                            </span>
                          </label>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleRemoveLogo}
                            className="text-red-400 hover:text-red-300 hover:bg-red-400/10 h-7 px-2"
                            data-testid="logo-remove-btn"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <label className="cursor-pointer block">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleLogoUpload}
                          data-testid="logo-upload-input"
                        />
                        <div className="flex items-center justify-center gap-2 p-4 bg-[#0A0A0A] rounded-lg border border-dashed border-[#27272A] hover:border-[#39FF14] transition-colors text-[#A1A1AA] hover:text-white">
                          {uploadingLogo ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4" />
                          )}
                          <span className="text-sm">{uploadingLogo ? "Uploading..." : "Upload Logo PNG"}</span>
                        </div>
                      </label>
                    )}

                    {/* Logo Position & Scale */}
                    {logoPreview && (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-[#A1A1AA] text-xs">Position</Label>
                          <Select value={logoPosition} onValueChange={setLogoPosition}>
                            <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#121212] border-[#27272A]">
                              <SelectItem value="top-left" className="text-white text-xs">Top Left</SelectItem>
                              <SelectItem value="top-right" className="text-white text-xs">Top Right</SelectItem>
                              <SelectItem value="bottom-left" className="text-white text-xs">Bottom Left</SelectItem>
                              <SelectItem value="bottom-right" className="text-white text-xs">Bottom Right</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[#A1A1AA] text-xs">Size ({Math.round(logoScale * 100)}%)</Label>
                          <input
                            type="range"
                            min="0.05"
                            max="0.35"
                            step="0.01"
                            value={logoScale}
                            onChange={(e) => setLogoScale(parseFloat(e.target.value))}
                            className="w-full h-2 rounded-lg appearance-none bg-[#27272A] accent-[#39FF14]"
                            data-testid="logo-scale-slider"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Style Presets */}
                  <div className="space-y-3">
                    <Label className="text-white">Style</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {STYLE_PRESETS.map((preset) => (
                        <Button
                          key={preset.name}
                          variant={selectedPreset === preset.name ? "default" : "outline"}
                          size="sm"
                          onClick={() => setSelectedPreset(preset.name)}
                          className={selectedPreset === preset.name 
                            ? "bg-[#39FF14] text-black hover:bg-[#32E512]" 
                            : "border-[#27272A] text-[#A1A1AA] hover:text-white hover:bg-[#27272A]"
                          }
                        >
                          {preset.name}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Custom Style Input */}
                  {selectedPreset === "Custom" && (
                    <div className="space-y-2">
                      <Label className="text-white">Custom Style Description</Label>
                      <Textarea
                        value={customStyle}
                        onChange={(e) => setCustomStyle(e.target.value)}
                        placeholder="Describe the style you want... e.g., 'Retro 80s vibe with sunset colors and palm trees'"
                        className="bg-[#0A0A0A] border-[#27272A] text-white min-h-[80px]"
                        data-testid="flyer-custom-style"
                      />
                    </div>
                  )}

                  {/* Toggles */}
                  <div className="space-y-4 pt-4 border-t border-[#27272A]">
                    <div className="flex items-center justify-between">
                      <Label className="text-white">Include Contact Info</Label>
                      <Switch
                        checked={includeContact}
                        onCheckedChange={setIncludeContact}
                        data-testid="flyer-include-contact"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-white">Include Services List</Label>
                      <Switch
                        checked={includeServices}
                        onCheckedChange={setIncludeServices}
                        data-testid="flyer-include-services"
                      />
                    </div>
                  </div>

                  {/* Generate Button */}
                  <Button
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className="w-full bg-[#39FF14] hover:bg-[#32E512] text-black font-bold py-6 text-lg"
                    data-testid="generate-flyer-btn"
                  >
                    {isGenerating ? (
                      <>
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        Generate Flyer
                      </>
                    )}
                  </Button>
                </div>

                {/* Preview Section */}
                <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6 space-y-6">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <FileImage className="w-5 h-5 text-[#39FF14]" />
                    Preview
                  </h2>

                  {/* Generated Image */}
                  <div className="aspect-square bg-[#0A0A0A] rounded-lg border border-[#27272A] flex items-center justify-center overflow-hidden">
                    {isGenerating ? (
                      <div className="text-center">
                        <RefreshCw className="w-12 h-12 text-[#39FF14] animate-spin mx-auto mb-4" />
                        <p className="text-[#A1A1AA]">Creating your flyer...</p>
                        <p className="text-[#A1A1AA] text-sm mt-1">This may take 15-30 seconds</p>
                      </div>
                    ) : generatedImage ? (
                      <img 
                        src={generatedImage} 
                        alt="Generated Flyer" 
                        className="w-full h-full object-contain"
                        data-testid="generated-flyer-image"
                      />
                    ) : (
                      <div className="text-center">
                        <Image className="w-16 h-16 text-[#27272A] mx-auto mb-4" />
                        <p className="text-[#A1A1AA]">Your generated flyer will appear here</p>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  {generatedImage && (
                    <div className="flex gap-3">
                      <Button
                        onClick={handleDownload}
                        className="flex-1 bg-[#39FF14] hover:bg-[#32E512] text-black font-bold"
                        data-testid="download-flyer-btn"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                    </div>
                  )}

                  {/* Facebook Caption */}
                  {generatedCaption && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-white flex items-center gap-2">
                          <Facebook className="w-4 h-4 text-blue-500" />
                          Facebook Caption
                        </Label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleCopyCaption}
                          className="text-[#39FF14] hover:text-[#32E512] hover:bg-[#39FF14]/10"
                          data-testid="copy-caption-btn"
                        >
                          {captionCopied ? (
                            <>
                              <Check className="w-4 h-4 mr-1" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4 mr-1" />
                              Copy
                            </>
                          )}
                        </Button>
                      </div>
                      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4 max-h-48 overflow-y-auto">
                        <pre className="text-[#EDEDED] text-sm whitespace-pre-wrap font-sans">
                          {generatedCaption}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Auto-Post Schedule Tab */}
            <TabsContent value="schedule">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Schedule Settings */}
                <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6 space-y-6">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#39FF14]" />
                    Auto-Post Schedule
                  </h2>

                  <p className="text-[#A1A1AA] text-sm">
                    Set up automatic flyer generation. When scheduled, flyers will be created and added to "Ready to Post" for you to share on Facebook.
                  </p>

                  {/* Enable/Disable Toggle */}
                  <div className="flex items-center justify-between p-4 bg-[#0A0A0A] rounded-lg border border-[#27272A]">
                    <div>
                      <Label className="text-white font-semibold">Enable Auto-Generation</Label>
                      <p className="text-[#A1A1AA] text-sm mt-1">Automatically create flyers on schedule</p>
                    </div>
                    <Switch
                      checked={scheduleEnabled}
                      onCheckedChange={setScheduleEnabled}
                      data-testid="schedule-enabled-toggle"
                    />
                  </div>

                  {/* Time Selection */}
                  <div className="space-y-2">
                    <Label className="text-white">Generation Time (Okinawa Time)</Label>
                    <Input
                      type="time"
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                      className="bg-[#0A0A0A] border-[#27272A] text-white"
                      data-testid="schedule-time"
                    />
                  </div>

                  {/* Days Selection */}
                  <div className="space-y-2">
                    <Label className="text-white">Days to Post</Label>
                    <div className="flex flex-wrap gap-2">
                      {DAYS_OF_WEEK.map((day) => (
                        <Button
                          key={day.value}
                          variant={scheduleDays.includes(day.value) ? "default" : "outline"}
                          size="sm"
                          onClick={() => toggleDay(day.value)}
                          className={scheduleDays.includes(day.value)
                            ? "bg-[#39FF14] text-black hover:bg-[#32E512]"
                            : "border-[#27272A] text-[#A1A1AA] hover:text-white hover:bg-[#27272A]"
                          }
                        >
                          {day.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Save & Generate Now Buttons */}
                  <div className="flex gap-3 pt-4">
                    <Button
                      onClick={handleSaveSchedule}
                      disabled={savingSchedule}
                      className="flex-1 bg-[#39FF14] hover:bg-[#32E512] text-black font-bold"
                      data-testid="save-schedule-btn"
                    >
                      {savingSchedule ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Settings
                    </Button>
                    <Button
                      onClick={handleGenerateNow}
                      disabled={isGenerating || headlinePool.length === 0}
                      variant="outline"
                      className="flex-1 border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black"
                      data-testid="generate-now-btn"
                    >
                      {isGenerating ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4 mr-2" />
                      )}
                      Generate Now
                    </Button>
                  </div>
                </div>

                {/* Content Pool */}
                <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6 space-y-6">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#39FF14]" />
                    Content Pool
                  </h2>

                  <p className="text-[#A1A1AA] text-sm">
                    Add headlines and offers. The system will randomly pick from this pool when auto-generating.
                  </p>

                  {/* Add New Content */}
                  <div className="space-y-3 p-4 bg-[#0A0A0A] rounded-lg border border-[#27272A]">
                    <Input
                      placeholder="Headline (e.g., SPRING SPECIAL!)"
                      value={newHeadline}
                      onChange={(e) => setNewHeadline(e.target.value)}
                      className="bg-[#121212] border-[#27272A] text-white"
                      data-testid="new-headline-input"
                    />
                    <Input
                      placeholder="Offer (e.g., 20% OFF First Cut)"
                      value={newOffer}
                      onChange={(e) => setNewOffer(e.target.value)}
                      className="bg-[#121212] border-[#27272A] text-white"
                      data-testid="new-offer-input"
                    />
                    <Button
                      onClick={handleAddToPool}
                      className="w-full bg-[#27272A] hover:bg-[#39FF14] hover:text-black text-white"
                      data-testid="add-to-pool-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add to Pool
                    </Button>
                  </div>

                  {/* Content Pool List */}
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {headlinePool.length === 0 ? (
                      <div className="text-center py-8 text-[#A1A1AA]">
                        <Sparkles className="w-12 h-12 mx-auto mb-2 opacity-30" />
                        <p>No content in pool yet</p>
                        <p className="text-sm">Add headlines and offers above</p>
                      </div>
                    ) : (
                      headlinePool.map((item, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg border border-[#27272A] group"
                        >
                          <div>
                            <p className="text-white font-semibold text-sm">{item.headline}</p>
                            <p className="text-[#A1A1AA] text-xs">{item.offer}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveFromPool(index)}
                            className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 hover:bg-red-400/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Ready to Post Tab */}
            <TabsContent value="pending">
              <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Facebook className="w-5 h-5 text-blue-500" />
                    Ready to Post on Facebook
                  </h2>
                  <Button
                    onClick={fetchPendingFlyers}
                    variant="ghost"
                    size="sm"
                    className="text-[#A1A1AA] hover:text-white"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>

                <p className="text-[#A1A1AA] text-sm mb-6">
                  These flyers are ready to be posted. Download the image and copy the caption to post on Facebook.
                </p>

                {loadingPending ? (
                  <div className="text-center py-12">
                    <RefreshCw className="w-8 h-8 text-[#39FF14] animate-spin mx-auto mb-4" />
                    <p className="text-[#A1A1AA]">Loading...</p>
                  </div>
                ) : pendingFlyers.length === 0 ? (
                  <div className="text-center py-12">
                    <Facebook className="w-16 h-16 text-[#27272A] mx-auto mb-4" />
                    <p className="text-[#A1A1AA]">No flyers ready to post</p>
                    <p className="text-[#A1A1AA] text-sm mt-1">Generate flyers from the Auto-Post tab or Create Flyer tab</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {pendingFlyers.map((flyer) => (
                      <div
                        key={flyer.flyer_id}
                        className="bg-[#0A0A0A] border border-[#27272A] rounded-lg overflow-hidden"
                      >
                        {/* Flyer Image */}
                        {flyer.image_data && (
                          <div className="aspect-square bg-[#121212]">
                            <img
                              src={`data:image/png;base64,${flyer.image_data}`}
                              alt={flyer.headline}
                              className="w-full h-full object-contain"
                            />
                          </div>
                        )}
                        
                        {/* Flyer Info */}
                        <div className="p-4 space-y-3">
                          <div>
                            <h3 className="text-white font-bold">{flyer.headline}</h3>
                            <p className="text-[#A1A1AA] text-sm">{flyer.offer_text}</p>
                            <p className="text-[#A1A1AA] text-xs mt-1">
                              Generated: {new Date(flyer.created_at).toLocaleString()}
                            </p>
                          </div>

                          {/* Caption Preview */}
                          {flyer.caption && (
                            <div className="bg-[#121212] rounded p-2 max-h-24 overflow-y-auto">
                              <pre className="text-[#A1A1AA] text-xs whitespace-pre-wrap font-sans">
                                {flyer.caption.slice(0, 150)}...
                              </pre>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex gap-2">
                            <Button
                              onClick={() => {
                                const link = document.createElement("a");
                                link.href = `data:image/png;base64,${flyer.image_data}`;
                                link.download = `zigzag-flyer-${flyer.flyer_id}.png`;
                                link.click();
                                toast.success("Flyer downloaded!");
                              }}
                              size="sm"
                              className="flex-1 bg-[#39FF14] hover:bg-[#32E512] text-black"
                            >
                              <Download className="w-3 h-3 mr-1" />
                              Download
                            </Button>
                            <Button
                              onClick={() => {
                                navigator.clipboard.writeText(flyer.caption);
                                toast.success("Caption copied!");
                              }}
                              size="sm"
                              variant="outline"
                              className="flex-1 border-[#27272A] text-white hover:bg-[#27272A]"
                            >
                              <Copy className="w-3 h-3 mr-1" />
                              Copy Caption
                            </Button>
                          </div>
                          <Button
                            onClick={() => handleMarkAsPosted(flyer.flyer_id)}
                            size="sm"
                            variant="ghost"
                            className="w-full text-[#A1A1AA] hover:text-[#39FF14] hover:bg-[#39FF14]/10"
                          >
                            <Check className="w-3 h-3 mr-1" />
                            Mark as Posted
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="history">
              <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
                <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <History className="w-5 h-5 text-[#39FF14]" />
                  Generated Flyers History
                </h2>

                {loadingHistory ? (
                  <div className="text-center py-12">
                    <RefreshCw className="w-8 h-8 text-[#39FF14] animate-spin mx-auto mb-4" />
                    <p className="text-[#A1A1AA]">Loading history...</p>
                  </div>
                ) : flyerHistory.length === 0 ? (
                  <div className="text-center py-12">
                    <FileImage className="w-16 h-16 text-[#27272A] mx-auto mb-4" />
                    <p className="text-[#A1A1AA]">No flyers generated yet</p>
                    <p className="text-[#A1A1AA] text-sm mt-1">Create your first flyer to see it here</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {flyerHistory.map((flyer) => (
                      <div 
                        key={flyer.flyer_id}
                        className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4 flex items-center justify-between"
                      >
                        <div>
                          <h3 className="text-white font-bold">{flyer.headline}</h3>
                          <p className="text-[#A1A1AA] text-sm">{flyer.offer_text}</p>
                          <p className="text-[#A1A1AA] text-xs mt-1">
                            {new Date(flyer.created_at).toLocaleDateString()} • {flyer.style_description?.slice(0, 50)}...
                          </p>
                        </div>
                        <div className="text-[#39FF14] text-sm">
                          Generated
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default FlyerGenerator;
